import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useDeferredValue, useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Search, SlidersHorizontal, X, Sun, Leaf } from 'lucide-react';
import { z } from 'zod';

import { ProductCard } from '@/components/product/product-card';
import { ProductGridSkeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useProducts, useCategories, type ProductFilters } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';

/**
 * Filters live in the URL, not in component state.
 *
 * That's what makes a filtered view shareable, bookmarkable, and survivable
 * across a refresh — and it's the reason for TanStack Router over React
 * Router here: the search params are typed and validated end to end.
 */
const searchSchema = z.object({
  category: z.string().optional(),
  q: z.string().optional(),
  sort: z.enum(['newest', 'price_asc', 'price_desc']).optional(),
  natural: z.boolean().optional(),
  clean: z.boolean().optional(),
});

export const Route = createFileRoute('/shop/')({
  validateSearch: searchSchema,
  component: ShopPage,
});

const SORTS = [
  { value: 'newest', label: 'Freshest first' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
] as const;

function ShopPage() {
  const search = Route.useSearch();
  // '/shop/' with the trailing slash — that's the generated route id for an
  // index route. Without it the search-param updaters below type as `never`.
  const navigate = useNavigate({ from: '/shop/' });

  const [searchInput, setSearchInput] = useState(search.q ?? '');
  // Keeps typing responsive: the input updates immediately, the (expensive)
  // grid re-render lags a frame behind rather than blocking each keystroke.
  const deferredSearch = useDeferredValue(searchInput);
  const [showFilters, setShowFilters] = useState(false);

  const { data: categories } = useCategories();

  const filters: ProductFilters = {
    category: search.category,
    search: deferredSearch,
    sort: search.sort ?? 'newest',
    naturalOnly: search.natural,
    chemicalFreeOnly: search.clean,
  };
  const { data: products, isLoading, isError } = useProducts(filters);

  // Push the debounced search term into the URL. 350ms — long enough not to
  // write a history entry per keystroke, short enough to feel live.
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((search.q ?? '') === searchInput) return;
      void navigate({
        search: (prev) => ({ ...prev, q: searchInput || undefined }),
        replace: true,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, search.q, navigate]);

  function setFilter<K extends keyof z.infer<typeof searchSchema>>(
    key: K,
    value: z.infer<typeof searchSchema>[K],
  ) {
    void navigate({ search: (prev) => ({ ...prev, [key]: value || undefined }) });
  }

  function clearAll() {
    setSearchInput('');
    void navigate({ search: {} });
  }

  const activeCount = [search.category, search.q, search.natural, search.clean].filter(
    Boolean,
  ).length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      {/* ── Header ── */}
      <div className="mb-8">
        <h1 className="font-display text-display font-black text-soil-900">The shop</h1>
        <p className="mt-2 text-soil-600">
          Everything here came from a farm we can name. Filter, or just browse.
        </p>
      </div>

      {/* ── Search + sort ── */}
      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative min-w-64 flex-1">
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-soil-400"
            aria-hidden
          />
          <input
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search mangoes, rice, tomatoes…"
            aria-label="Search produce"
            className="h-11 w-full rounded-xl border border-soil-200 bg-white pl-10 pr-4 text-sm placeholder:text-soil-400 focus:border-farm-500 focus:outline-none focus:ring-4 focus:ring-farm-500/12"
          />
        </div>

        <select
          value={search.sort ?? 'newest'}
          onChange={(event) => setFilter('sort', event.target.value as ProductFilters['sort'])}
          aria-label="Sort by"
          className="h-11 cursor-pointer rounded-xl border border-soil-200 bg-white px-4 text-sm focus:border-farm-500 focus:outline-none focus:ring-4 focus:ring-farm-500/12"
        >
          {SORTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <Button
          variant={showFilters || activeCount > 0 ? 'primary' : 'outline'}
          onClick={() => setShowFilters((open) => !open)}
          aria-expanded={showFilters}
        >
          <SlidersHorizontal aria-hidden />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 flex size-5 items-center justify-center rounded-full bg-white/25 text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </Button>
      </div>

      {/* ── Filter panel ── */}
      <AnimatePresence initial={false}>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transitions.base}
            className="overflow-hidden"
          >
            <div className="mb-5 space-y-4 rounded-card border border-soil-200 bg-white p-5">
              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-soil-500">
                  Category
                </p>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    active={!search.category}
                    onClick={() => setFilter('category', undefined)}
                    label="Everything"
                  />
                  {categories?.map((category) => (
                    <FilterChip
                      key={category.id}
                      active={search.category === category.slug}
                      onClick={() =>
                        setFilter(
                          'category',
                          search.category === category.slug ? undefined : category.slug,
                        )
                      }
                      label={category.name}
                    />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-soil-500">
                  How it was grown
                </p>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    active={Boolean(search.natural)}
                    onClick={() => setFilter('natural', search.natural ? undefined : true)}
                    label="Naturally ripened"
                    icon={<Sun className="size-3" aria-hidden />}
                  />
                  <FilterChip
                    active={Boolean(search.clean)}
                    onClick={() => setFilter('clean', search.clean ? undefined : true)}
                    label="No chemicals"
                    icon={<Leaf className="size-3" aria-hidden />}
                  />
                </div>
              </div>

              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="flex items-center gap-1.5 text-sm font-medium text-soil-500 transition-colors hover:text-danger"
                >
                  <X className="size-3.5" aria-hidden />
                  Clear all filters
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Results ── */}
      <p className="mb-5 text-sm text-soil-500" aria-live="polite">
        {isLoading
          ? 'Looking…'
          : `${products?.length ?? 0} ${products?.length === 1 ? 'item' : 'items'}`}
      </p>

      {isLoading ? (
        <ProductGridSkeleton count={8} />
      ) : isError ? (
        <Empty
          title="Couldn't load the shop"
          body="Something went wrong on our side. Try refreshing."
          action={<Button onClick={() => window.location.reload()}>Refresh</Button>}
        />
      ) : products && products.length > 0 ? (
        // layout on the grid + layout on each card means filtering reflows
        // smoothly instead of snapping.
        <motion.div
          layout
          className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4"
        >
          <AnimatePresence mode="popLayout">
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <Empty
          title="Nothing matches that"
          body={
            activeCount > 0
              ? 'Try loosening a filter — the catalog changes with the season.'
              : 'Produce is added as farms are verified. Check back soon.'
          }
          action={
            activeCount > 0 ? (
              <Button variant="outline" onClick={clearAll}>
                Clear filters
              </Button>
            ) : (
              <Link to="/">
                <Button variant="outline">Back home</Button>
              </Link>
            )
          }
        />
      )}
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-3.5 py-1.5 text-sm font-medium transition-all active:scale-95',
        active
          ? 'bg-farm-600 text-white shadow-soft'
          : 'bg-soil-100 text-soil-700 hover:bg-soil-200',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function Empty({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="rounded-card border border-dashed border-soil-300 bg-white/50 p-16 text-center">
      <Badge variant="neutral" size="lg" className="mx-auto">
        <Leaf aria-hidden />
      </Badge>
      <h2 className="mt-4 font-display text-xl font-bold text-soil-800">{title}</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-soil-500">{body}</p>
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}
